import { Workspace, WorkspaceUsage } from "../types";
import { isSSR } from "../../scripts/react/utils";
import React from "react";

export type UniverseAlert = {
    id: string;
    content: string | React.ReactNode;
    cta?: string | React.ReactNode;
    icon?: string;
    type: "info";
    mutedUntil?: number;
    priority?: number;
    muteDuration?: number;
};

export interface UniverseState {
    userAsWorkspace: Workspace | null;
    userAsUsage: WorkspaceUsage | null;
    universeAlerts: UniverseAlert[];
    // getters
    getUserAsWorkspace: () => Workspace | null;
    getUserAsUsage: () => WorkspaceUsage | null;
    getUniverseAlerts: () => UniverseAlert[];
    // mutators
    setUserAsWorkspace: (workspace: Workspace) => void;
    setUserAsUsage: (usage: WorkspaceUsage) => void;
    setUniverseAlerts: (alerts: UniverseAlert[]) => void;
}

const initialState: Partial<UniverseState> = {
    userAsUsage: isSSR()
        ? null
        : (JSON.parse(localStorage.getItem("userAsUsage") as string) as WorkspaceUsage),
    userAsWorkspace: isSSR()
        ? null
        : (JSON.parse(localStorage.getItem("userAsWorkspace") as string) as Workspace),
    universeAlerts: isSSR()
        ? []
        : (JSON.parse(localStorage.getItem("universeAlerts") as string) as UniverseAlert[])
};

const getters = (set, get): Partial<UniverseState> => ({
    getUserAsWorkspace: () => get().userAsWorkspace,
    getUserAsUsage: () => get().userAsUsage,
    getUniverseAlerts: () => get().universeAlerts ?? []
});

const mutators = (set, get): Partial<UniverseState> => ({
    setUserAsWorkspace: (workspace: Workspace) => {
        set(() => ({ userAsWorkspace: workspace }));
        if (!isSSR()) {
            localStorage.setItem("userAsWorkspace", JSON.stringify(workspace));
        }
    },
    setUserAsUsage: (usage: WorkspaceUsage) => {
        set(() => ({ userAsUsage: usage }));
        if (!isSSR()) {
            localStorage.setItem("userAsUsage", JSON.stringify(usage));
        }
    },
    setUniverseAlerts: (alerts: UniverseAlert[]) => {
        set(() => ({ universeAlerts: alerts }));
        if (!isSSR()) {
            localStorage.setItem("universeAlerts", JSON.stringify(alerts));
        }
    }
});

function universeReducer(set, get): UniverseState {
    return {
        ...(initialState as Partial<UniverseState>),
        ...(getters(set, get) as Partial<UniverseState>),
        ...(mutators(set, get) as Partial<UniverseState>)
    } as UniverseState;
}

export function buildUniverseStore(set, get, api): UniverseState {
    return { ...universeReducer(set, get) };
}
